import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Camera, ScanLine, Keyboard, ArrowLeft, Loader2 } from 'lucide-react';

const resolveQrTarget = (rawValue) => {
  const value = String(rawValue || '').trim();
  if (!value) return null;

  if (value.startsWith('http://') || value.startsWith('https://')) {
    const url = new URL(value);
    return `${url.pathname}${url.search}`;
  }

  if (value.startsWith('/')) {
    return value;
  }

  if (value.includes('/qr/animal/')) {
    const markerIndex = value.indexOf('/qr/animal/');
    return value.slice(markerIndex);
  }

  return `/qr/animal/${value}`;
};

export default function QRScannerPage() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const scanTimerRef = useRef(null);

  const [manualValue, setManualValue] = useState('');
  const [startingCamera, setStartingCamera] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraSupported, setCameraSupported] = useState(false);

  const cameraSupportLabel = useMemo(() => {
    if (typeof window === 'undefined') return 'Indisponível';
    if (!('BarcodeDetector' in window) || !navigator.mediaDevices?.getUserMedia) {
      return 'Navegador sem suporte ao leitor por câmera';
    }
    return 'Leitor por câmera disponível';
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    setCameraSupported('BarcodeDetector' in window && Boolean(navigator.mediaDevices?.getUserMedia));
    return () => {
      if (scanTimerRef.current) {
        window.clearInterval(scanTimerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const stopCamera = () => {
    if (scanTimerRef.current) {
      window.clearInterval(scanTimerRef.current);
      scanTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const openResolvedTarget = (rawValue) => {
    const target = resolveQrTarget(rawValue);
    if (!target) {
      toast.error('QR Code inválido');
      return;
    }
    stopCamera();
    navigate(target);
  };

  const startCamera = async () => {
    if (!cameraSupported) {
      toast.error('Este navegador não suporta leitura de QR por câmera');
      return;
    }

    setStartingCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false
      });
      streamRef.current = stream;

      if (!detectorRef.current) {
        detectorRef.current = new window.BarcodeDetector({ formats: ['qr_code'] });
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      scanTimerRef.current = window.setInterval(async () => {
        if (!videoRef.current || !canvasRef.current || !detectorRef.current) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (video.readyState < 2) return;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d');
        if (!context) return;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        try {
          const barcodes = await detectorRef.current.detect(canvas);
          if (barcodes.length > 0 && barcodes[0].rawValue) {
            openResolvedTarget(barcodes[0].rawValue);
          }
        } catch (error) {
          stopCamera();
          toast.error('Falha ao ler QR Code pela câmera');
        }
      }, 700);

      setCameraActive(true);
    } catch (error) {
      toast.error('Não foi possível acessar a câmera');
    } finally {
      setStartingCamera(false);
    }
  };

  const handleManualSubmit = (event) => {
    event.preventDefault();
    openResolvedTarget(manualValue);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-teal-50 px-4 py-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
          <Button variant="outline" onClick={() => navigate('/animals')}>
            Buscar animal
          </Button>
        </div>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-slate-800">
              <ScanLine className="h-5 w-5" />
              Leitor de QR Code
            </CardTitle>
            <p className="text-sm text-slate-500">
              Leia a etiqueta do animal para abrir a ficha correta e registrar alimentação, medicação ou acompanhamento.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="rounded border border-slate-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-800">Leitura por câmera</p>
                  <p className="text-sm text-slate-500">{cameraSupportLabel}</p>
                </div>
                {!cameraActive ? (
                  <Button onClick={startCamera} disabled={!cameraSupported || startingCamera}>
                    {startingCamera ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />}
                    Ativar câmera
                  </Button>
                ) : (
                  <Button variant="outline" onClick={stopCamera}>
                    Parar câmera
                  </Button>
                )}
              </div>
              <div className="overflow-hidden rounded-lg border border-dashed border-slate-300 bg-slate-100">
                <video ref={videoRef} className="aspect-video w-full object-cover" muted playsInline />
                <canvas ref={canvasRef} className="hidden" />
              </div>
            </div>

            <Card className="border-emerald-200 bg-emerald-50/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base text-emerald-800">
                  <Keyboard className="h-4 w-4" />
                  Entrada manual ou leitor USB
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleManualSubmit} className="space-y-3">
                  <div>
                    <Label>Código ou link do QR</Label>
                    <Input
                      value={manualValue}
                      onChange={(event) => setManualValue(event.target.value)}
                      placeholder="Cole o link do QR ou escaneie com leitor USB"
                    />
                  </div>
                  <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                    Abrir animal
                  </Button>
                </form>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
